import type {
  ShopperAiTagId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import {
  MongoCustomerId,
  MongoUserId,
} from "@redotech/effect-api-shared/ids/mongo-ids";
import type {
  SupportTicketListItemResponse,
  SupportTicketPriority,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { SPAM_TAG } from "@redotech/redo-model/conversation-tags/conversation-tag-record";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import {
  applySupportTicketAssignee,
  applySupportTicketCustomer,
  applySupportTicketPriority,
  applySupportTicketReadState,
  applySupportTicketSnooze,
  applySupportTicketStatus,
  replaceSupportTicketTags,
  type SnoozeParams,
} from "@redotech/redo-support-v2/actions/support-ticket-action-mutations";
import type { BoundAction } from "@redotech/redo-web/actions/action-definition";
import type { ActionId } from "@redotech/redo-web/actions/action-ids";
import { useActionScope } from "@redotech/redo-web/actions/action-scope-context";
import {
  supportTicketActionIds,
  type SupportTicketActionId,
} from "@redotech/redo-web/actions/support-ticket-actions/action-ids";
import {
  addTagScope,
  assignScope,
  priorityScope,
  snoozeScope,
} from "@redotech/redo-web/actions/support-ticket-actions/scope-trees";
import { toast } from "@redotech/redo-web/alert";
import { useLiveQuery } from "@tanstack/react-db";
import { Effect } from "effect";
import { useMemo } from "react";
import { merchantQueryClient } from "../../react-query-client";
import { catchApiErrors } from "../db/error-handling";
import { useCloseTicketNavigation } from "../hooks/use-close-ticket-navigation";
import { invalidateSupportTicketListQueriesAfterDelay } from "../invalidate-support-ticket-list-queries";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";
import { getMissingRequiredTagGroups } from "../utils/tag-group-close-validation";
import { actionRegistry } from "./action-registry";

/**
 * A support ticket action with a bound execute function.
 */
export type SupportTicketAction<TParams = void> = BoundAction<
  SupportTicketListItemResponse,
  SupportTicketActionId,
  TParams,
  SupportTicketId
>;

export interface AssignSupportTicketParams {
  assigneeId: MongoUserId | null;
}

export interface AddTagSupportTicketParams {
  tagIds: ShopperAiTagId[];
}

export interface ChangeCustomerSupportTicketParams {
  mongoCustomerId: MongoCustomerId | null;
}

export type SetPriorityParams = SupportTicketPriority | null;

/**
 * Hook that returns the close support ticket action with a bound handler.
 * Includes permission check in isEnabled.
 */
export function useCloseSupportTicketAction(): SupportTicketAction {
  const collections = useGlobalSupportTicketCollections();
  const canClose = useHasPermission(SupportPermissions.CLOSE_CONVERSATION);
  const navigateAfterClose = useCloseTicketNavigation();

  const tagGroupsResult = useLiveQuery(
    (q) => q.from({ tagGroup: collections.tagGroups }),
    [collections.tagGroups],
  );
  const tagsResult = useLiveQuery(
    (q) => q.from({ tag: collections.tags }),
    [collections.tags],
  );

  const execute = useHandler((supportTicketId: SupportTicketId) => {
    const ticket =
      collections.supportTicketsList.collection.get(supportTicketId);
    if (ticket) {
      const missing = getMissingRequiredTagGroups(
        ticket.tagIds,
        tagsResult.data,
        tagGroupsResult.data,
      );
      if (missing.length > 0) {
        const groupNames = missing.map((g) => g.name).join(", ");
        toast(
          `This support ticket cannot be closed because it's missing required tags from: ${groupNames}. Please add tags from these groups before closing.`,
          { variant: "error" },
        );
        return;
      }
    }
    collections.supportTicketsList.collection.update(supportTicketId, (draft) =>
      applySupportTicketStatus(draft, "CLOSED"),
    );
    invalidateSupportTicketListQueriesAfterDelay({
      queryClient: merchantQueryClient,
      queryScope: collections.supportTicketsList.queryScope,
    });
    navigateAfterClose(supportTicketId);
  });

  const registryAction = actionRegistry[supportTicketActionIds.close];

  return useMemo(
    () => ({
      ...registryAction,
      execute,
      isEnabled: (context: SupportTicketListItemResponse) => {
        if (!canClose) {
          return false;
        }
        return registryAction.isEnabled?.(context) ?? true;
      },
    }),
    [execute, registryAction, canClose],
  );
}

/**
 * Hook that returns the reopen support ticket action with a bound handler.
 * Includes permission check in isEnabled.
 */
export function useReopenSupportTicketAction(): SupportTicketAction {
  const collections = useGlobalSupportTicketCollections();
  const canClose = useHasPermission(SupportPermissions.CLOSE_CONVERSATION);

  const execute = useHandler((supportTicketId: SupportTicketId) => {
    collections.supportTicketsList.collection.update(supportTicketId, (draft) =>
      applySupportTicketStatus(draft, "OPEN"),
    );
    invalidateSupportTicketListQueriesAfterDelay({
      queryClient: merchantQueryClient,
      queryScope: collections.supportTicketsList.queryScope,
    });
  });

  const registryAction = actionRegistry[supportTicketActionIds.reopen];

  return useMemo(
    () => ({
      ...registryAction,
      execute,
      isEnabled: (context: SupportTicketListItemResponse) => {
        if (!canClose) {
          return false;
        }
        return registryAction.isEnabled?.(context) ?? true;
      },
    }),
    [execute, registryAction, canClose],
  );
}

/**
 * Hook that returns the snooze support ticket action with a bound handler.
 */
export function useSnoozeSupportTicketAction(): SupportTicketAction<SnoozeParams> {
  const collections = useGlobalSupportTicketCollections();

  const execute = useHandler(
    (supportTicketId: SupportTicketId, option: SnoozeParams) => {
      collections.supportTicketsList.collection.update(
        supportTicketId,
        (draft) => applySupportTicketSnooze(draft, option),
      );
      invalidateSupportTicketListQueriesAfterDelay({
        queryClient: merchantQueryClient,
        queryScope: collections.supportTicketsList.queryScope,
      });
    },
  );

  return useMemo(
    () => ({ ...actionRegistry[supportTicketActionIds.snooze], execute }),
    [execute],
  );
}

export function useSnoozeSupportTicketOptions(): readonly SupportTicketAction[] {
  const setSnooze = useSnoozeSupportTicketAction();

  return useMemo(
    () => [
      {
        ...actionRegistry[supportTicketActionIds.snooze1h],
        execute: (supportTicketId: SupportTicketId) =>
          setSnooze.execute(supportTicketId, { duration: "1h" }),
      },
      {
        ...actionRegistry[supportTicketActionIds.snooze4h],
        execute: (supportTicketId: SupportTicketId) =>
          setSnooze.execute(supportTicketId, { duration: "4h" }),
      },
      {
        ...actionRegistry[supportTicketActionIds.snooze24h],
        execute: (supportTicketId: SupportTicketId) =>
          setSnooze.execute(supportTicketId, { duration: "24h" }),
      },
      {
        ...actionRegistry[supportTicketActionIds.snoozeCustom],
        // custom is handled by the modal — this is intentionally a no-op
        execute: (_supportTicketId: SupportTicketId) => undefined,
      },
      {
        ...actionRegistry[supportTicketActionIds.snoozeClear],
        execute: (supportTicketId: SupportTicketId) =>
          setSnooze.execute(supportTicketId, null),
      },
    ],
    [setSnooze],
  );
}

/**
 * Hook that returns the mark read support ticket action with a bound handler.
 */
export function useMarkReadSupportTicketAction(): SupportTicketAction {
  const collections = useGlobalSupportTicketCollections();

  const execute = useHandler((supportTicketId: SupportTicketId) => {
    collections.supportTicketsList.collection.update(supportTicketId, (draft) =>
      applySupportTicketReadState(draft, new Date()),
    );
    invalidateSupportTicketListQueriesAfterDelay({
      queryClient: merchantQueryClient,
      queryScope: collections.supportTicketsList.queryScope,
    });
  });

  return useMemo(
    () => ({ ...actionRegistry[supportTicketActionIds.markRead], execute }),
    [execute],
  );
}

/**
 * Hook that returns the mark unread support ticket action with a bound handler.
 */
export function useMarkUnreadSupportTicketAction(): SupportTicketAction {
  const collections = useGlobalSupportTicketCollections();

  const execute = useHandler((supportTicketId: SupportTicketId) => {
    collections.supportTicketsList.collection.update(supportTicketId, (draft) =>
      applySupportTicketReadState(draft, null),
    );
    invalidateSupportTicketListQueriesAfterDelay({
      queryClient: merchantQueryClient,
      queryScope: collections.supportTicketsList.queryScope,
    });
  });

  return useMemo(
    () => ({ ...actionRegistry[supportTicketActionIds.markUnread], execute }),
    [execute],
  );
}

/**
 * Hook that returns the archive support ticket action with a bound handler.
 * Includes permission check in isEnabled.
 */
export function useArchiveSupportTicketAction(): SupportTicketAction {
  const collections = useGlobalSupportTicketCollections();
  const canArchive = useHasPermission(SupportPermissions.ARCHIVE_CONVERSATION);

  const execute = useHandler((supportTicketId: SupportTicketId) => {
    collections.supportTicketsList.collection.update(supportTicketId, (draft) =>
      applySupportTicketStatus(draft, "ARCHIVED"),
    );
    invalidateSupportTicketListQueriesAfterDelay({
      queryClient: merchantQueryClient,
      queryScope: collections.supportTicketsList.queryScope,
    });
  });

  const registryAction = actionRegistry[supportTicketActionIds.archive];

  return useMemo(
    () => ({
      ...registryAction,
      execute,
      isEnabled: (context: SupportTicketListItemResponse) => {
        if (!canArchive) {
          return false;
        }
        return registryAction.isEnabled?.(context) ?? true;
      },
    }),
    [execute, registryAction, canArchive],
  );
}

/**
 * Hook that returns the delete support ticket action with a bound handler.
 */
export function useDeleteSupportTicketAction(): SupportTicketAction {
  const collections = useGlobalSupportTicketCollections();

  const execute = useHandler((supportTicketId: SupportTicketId) => {
    collections.supportTicketsList.collection.update(supportTicketId, (draft) =>
      applySupportTicketStatus(draft, "DELETED"),
    );
    invalidateSupportTicketListQueriesAfterDelay({
      queryClient: merchantQueryClient,
      queryScope: collections.supportTicketsList.queryScope,
    });
  });

  return useMemo(
    () => ({ ...actionRegistry[supportTicketActionIds.delete], execute }),
    [execute],
  );
}

/**
 * Hook that returns the set priority support ticket action with a bound handler.
 */
export function useSetPrioritySupportTicketAction(): SupportTicketAction<SetPriorityParams> {
  const collections = useGlobalSupportTicketCollections();

  const execute = useHandler(
    (supportTicketId: SupportTicketId, priority: SetPriorityParams) => {
      collections.supportTicketsList.collection.update(
        supportTicketId,
        (draft) => applySupportTicketPriority(draft, priority),
      );
      invalidateSupportTicketListQueriesAfterDelay({
        queryClient: merchantQueryClient,
        queryScope: collections.supportTicketsList.queryScope,
      });
    },
  );

  return useMemo(
    () => ({ ...actionRegistry[supportTicketActionIds.setPriority], execute }),
    [execute],
  );
}

/**
 * Hook that returns all priority option actions with the value baked in.
 * These are void actions that can be executed directly without params.
 */
export function usePriorityOptionActions(): readonly SupportTicketAction[] {
  const setPriority = useSetPrioritySupportTicketAction();

  return useMemo(
    () => [
      {
        ...actionRegistry[supportTicketActionIds.priorityUrgent],
        execute: (supportTicketId: SupportTicketId) =>
          setPriority.execute(supportTicketId, "URGENT"),
      },
      {
        ...actionRegistry[supportTicketActionIds.priorityClear],
        execute: (supportTicketId: SupportTicketId) =>
          setPriority.execute(supportTicketId, null),
      },
    ],
    [setPriority],
  );
}

/**
 * Hook that returns the assign support ticket action with a bound handler.
 * Note: This requires the assignee ID to be passed as a param.
 * The actual UI for selecting an assignee should be handled separately.
 */
export function useAssignSupportTicketAction(): SupportTicketAction<AssignSupportTicketParams> {
  const collections = useGlobalSupportTicketCollections();
  const canEditAssignee = useHasPermission(SupportPermissions.EDIT_ASSIGNEE);

  const execute = useHandler(
    (supportTicketId: SupportTicketId, params: AssignSupportTicketParams) => {
      collections.supportTicketsList.collection.update(
        supportTicketId,
        (draft) => applySupportTicketAssignee(draft, params.assigneeId),
      );
      invalidateSupportTicketListQueriesAfterDelay({
        queryClient: merchantQueryClient,
        queryScope: collections.supportTicketsList.queryScope,
      });
    },
  );

  const registryAction = actionRegistry[supportTicketActionIds.assign];

  return useMemo(
    () => ({
      ...registryAction,
      execute,
      isEnabled: (context: SupportTicketListItemResponse) => {
        if (!canEditAssignee) {
          return false;
        }
        return registryAction.isEnabled?.(context) ?? true;
      },
    }),
    [execute, registryAction, canEditAssignee],
  );
}

/**
 * Hook that returns the assign-to-me support ticket action with a bound handler.
 * Assigns the support ticket to the currently logged-in user.
 */
export function useAssignToMeSupportTicketAction(): SupportTicketAction {
  const assignAction = useAssignSupportTicketAction();
  const user = useRequiredContext(UserContext);
  const canEditAssignee = useHasPermission(SupportPermissions.EDIT_ASSIGNEE);

  const execute = useHandler((supportTicketId: SupportTicketId) => {
    void assignAction.execute(supportTicketId, {
      assigneeId: MongoUserId.fromString(user._id),
    });
  });

  const registryAction = actionRegistry[supportTicketActionIds.assignToMe];

  return useMemo(
    () => ({
      ...registryAction,
      execute,
      isEnabled: (context: SupportTicketListItemResponse) => {
        if (!canEditAssignee) {
          return false;
        }
        return registryAction.isEnabled?.(context) ?? true;
      },
    }),
    [execute, registryAction, canEditAssignee],
  );
}

/**
 * Hook that returns the add tag support ticket action with a bound handler.
 * Note: This requires tag IDs to be passed as a param.
 * The actual UI for selecting tags should be handled separately.
 */
export function useAddTagSupportTicketAction(): SupportTicketAction<AddTagSupportTicketParams> {
  const collections = useGlobalSupportTicketCollections();

  const execute = useHandler(
    (supportTicketId: SupportTicketId, params: AddTagSupportTicketParams) => {
      collections.supportTicketsList.collection.update(
        supportTicketId,
        (draft) => replaceSupportTicketTags(draft, params.tagIds),
      );
      invalidateSupportTicketListQueriesAfterDelay({
        queryClient: merchantQueryClient,
        queryScope: collections.supportTicketsList.queryScope,
      });
    },
  );

  return useMemo(
    () => ({ ...actionRegistry[supportTicketActionIds.addTag], execute }),
    [execute],
  );
}

/**
 * Hook that returns the change customer support ticket action with a bound handler.
 * Note: This requires the customer ID to be passed as a param.
 * The actual UI for selecting a customer should be handled separately.
 */
export function useChangeCustomerSupportTicketAction(): {
  execute: (
    supportTicketId: SupportTicketId,
    params: ChangeCustomerSupportTicketParams,
  ) => void;
} {
  const collections = useGlobalSupportTicketCollections();

  const execute = useHandler(
    (
      supportTicketId: SupportTicketId,
      params: ChangeCustomerSupportTicketParams,
    ) => {
      collections.supportTicketsList.collection.update(
        supportTicketId,
        (draft) => applySupportTicketCustomer(draft, params.mongoCustomerId),
      );
      invalidateSupportTicketListQueriesAfterDelay({
        queryClient: merchantQueryClient,
        queryScope: collections.supportTicketsList.queryScope,
      });
    },
  );

  return useMemo(() => ({ execute }), [execute]);
}

/**
 * Hook that returns the spam tag id for the current account, or null if no
 * tag named SPAM_TAG exists yet (the server creates it on demand when a
 * conversation is first marked spam).
 */
export function useSpamTagId(): ShopperAiTagId | null {
  const collections = useGlobalSupportTicketCollections();
  const tagsResult = useLiveQuery(
    (q) => q.from({ tag: collections.tags }),
    [collections.tags],
  );
  return useMemo(
    () => tagsResult.data.find((tag) => tag.name === SPAM_TAG)?.id ?? null,
    [tagsResult.data],
  );
}

/**
 * Returns true if the support ticket carries the spam tag.
 */
export function useIsSupportTicketSpam(
  supportTicket: SupportTicketListItemResponse,
): boolean {
  const spamTagId = useSpamTagId();
  return spamTagId !== null && supportTicket.tagIds.includes(spamTagId);
}

/**
 * Hook that returns the mark-as-spam action with a bound handler.
 *
 * The actual side effects — close the ticket, tag it with `SPAM_TAG`
 * (find-or-created on demand), and add the customer's primary email to the
 * `archive-emails-from-blocked-senders` default-rule config — all run inside
 * the support-ticket service via the `markSupportTicketSpam` RPC, so the
 * provider-flagged ingest path and the merchant-triggered Mark-as-spam menu
 * share one implementation. The hook is responsible only for an optimistic
 * row update + invalidation.
 */
export function useMarkSpamSupportTicketAction(): SupportTicketAction {
  const collections = useGlobalSupportTicketCollections();
  const effectApi = useRequiredContext(EffectApiClientContext);
  const spamTagId = useSpamTagId();
  const canClose = useHasPermission(SupportPermissions.CLOSE_CONVERSATION);

  const execute = useHandler(async (supportTicketId: SupportTicketId) => {
    collections.supportTicketsList.collection.update(
      supportTicketId,
      (draft) => {
        applySupportTicketStatus(draft, "CLOSED");
        if (spamTagId && !draft.tagIds.includes(spamTagId)) {
          replaceSupportTicketTags(draft, [...draft.tagIds, spamTagId]);
        }
      },
    );

    await effectApi.runPromise(
      catchApiErrors(
        effectApi
          .getClient()
          .supportTicket.markSupportTicketSpam({
            path: { id: supportTicketId },
          })
          .pipe(Effect.asVoid),
      ),
    );

    invalidateSupportTicketListQueriesAfterDelay({
      queryClient: merchantQueryClient,
      queryScope: collections.supportTicketsList.queryScope,
    });
  });

  const registryAction = actionRegistry[supportTicketActionIds.markSpam];

  return useMemo(
    () => ({
      ...registryAction,
      execute,
      isEnabled: (context: SupportTicketListItemResponse) => {
        if (!canClose) {
          return false;
        }
        return registryAction.isEnabled?.(context) ?? true;
      },
    }),
    [execute, registryAction, canClose],
  );
}

/**
 * Hook that returns the unmark-as-spam action with a bound handler. Reverses
 * `useMarkSpamSupportTicketAction` — same shared server-side path, this time
 * via the `unmarkSupportTicketSpam` RPC, which reopens the ticket, removes
 * the spam tag, and removes the customer's email from the blocked list.
 */
export function useUnmarkSpamSupportTicketAction(): SupportTicketAction {
  const collections = useGlobalSupportTicketCollections();
  const effectApi = useRequiredContext(EffectApiClientContext);
  const spamTagId = useSpamTagId();
  const canClose = useHasPermission(SupportPermissions.CLOSE_CONVERSATION);

  const execute = useHandler(async (supportTicketId: SupportTicketId) => {
    collections.supportTicketsList.collection.update(
      supportTicketId,
      (draft) => {
        applySupportTicketStatus(draft, "OPEN");
        if (spamTagId) {
          replaceSupportTicketTags(
            draft,
            draft.tagIds.filter((id) => id !== spamTagId),
          );
        }
      },
    );

    await effectApi.runPromise(
      catchApiErrors(
        effectApi
          .getClient()
          .supportTicket.unmarkSupportTicketSpam({
            path: { id: supportTicketId },
          })
          .pipe(Effect.asVoid),
      ),
    );

    invalidateSupportTicketListQueriesAfterDelay({
      queryClient: merchantQueryClient,
      queryScope: collections.supportTicketsList.queryScope,
    });
  });

  const registryAction = actionRegistry[supportTicketActionIds.unmarkSpam];

  return useMemo(
    () => ({
      ...registryAction,
      execute,
      isEnabled: (context: SupportTicketListItemResponse) => {
        if (!canClose) {
          return false;
        }
        return registryAction.isEnabled?.(context) ?? true;
      },
    }),
    [execute, registryAction, canClose],
  );
}

/**
 * All bound support ticket actions.
 */
export interface SupportTicketActions {
  close: SupportTicketAction;
  reopen: SupportTicketAction;
  markInProgress: SupportTicketAction;
  snooze: SupportTicketAction<SnoozeParams>;
  markRead: SupportTicketAction;
  markUnread: SupportTicketAction;
  archive: SupportTicketAction;
  delete: SupportTicketAction;
  setPriority: SupportTicketAction<SetPriorityParams>;
  assign: SupportTicketAction<AssignSupportTicketParams>;
  addTag: SupportTicketAction<AddTagSupportTicketParams>;
}

/**
 * Hook that returns all support ticket actions with bound handlers.
 * Useful for command palette, action menus, etc.
 */
export function useSupportTicketActions(): SupportTicketActions {
  const close = useCloseSupportTicketAction();
  const reopen = useReopenSupportTicketAction();
  const markInProgress = useMarkInProgressSupportTicketAction();
  const snooze = useSnoozeSupportTicketAction();
  const markRead = useMarkReadSupportTicketAction();
  const markUnread = useMarkUnreadSupportTicketAction();
  const archive = useArchiveSupportTicketAction();
  const deleteAction = useDeleteSupportTicketAction();
  const setPriority = useSetPrioritySupportTicketAction();
  const assign = useAssignSupportTicketAction();
  const addTag = useAddTagSupportTicketAction();

  return useMemo(
    () => ({
      close,
      reopen,
      markInProgress,
      snooze,
      markRead,
      markUnread,
      archive,
      delete: deleteAction,
      setPriority,
      assign,
      addTag,
    }),
    [
      close,
      reopen,
      markInProgress,
      snooze,
      markRead,
      markUnread,
      archive,
      deleteAction,
      setPriority,
      assign,
      addTag,
    ],
  );
}

/**
 * Hook that returns the mark in progress support ticket action with a bound handler.
 * Includes permission check in isEnabled.
 */
export function useMarkInProgressSupportTicketAction(): SupportTicketAction {
  const collections = useGlobalSupportTicketCollections();
  const canClose = useHasPermission(SupportPermissions.CLOSE_CONVERSATION);

  const execute = useHandler((supportTicketId: SupportTicketId) => {
    collections.supportTicketsList.collection.update(supportTicketId, (draft) =>
      applySupportTicketStatus(draft, "IN_PROGRESS"),
    );
    invalidateSupportTicketListQueriesAfterDelay({
      queryClient: merchantQueryClient,
      queryScope: collections.supportTicketsList.queryScope,
    });
  });

  const registryAction = actionRegistry[supportTicketActionIds.markInProgress];

  return useMemo(
    () => ({
      ...registryAction,
      execute,
      isEnabled: (context: SupportTicketListItemResponse) => {
        if (!canClose) {
          return false;
        }
        return registryAction.isEnabled?.(context) ?? true;
      },
    }),
    [execute, registryAction, canClose],
  );
}

/**
 * Hook that returns the send message action with a bound handler.
 */
export function useSendMessageAction(
  onSend: () => void | Promise<void>,
): SupportTicketAction {
  const execute = useHandler(() => {
    void onSend();
  });

  return useMemo(
    () => ({ ...actionRegistry[supportTicketActionIds.sendMessage], execute }),
    [execute],
  );
}

/**
 * Hook that returns the delete draft action with a bound handler.
 */
export function useDeleteDraftAction(
  onDelete: () => void | Promise<void>,
): SupportTicketAction {
  const execute = useHandler(() => {
    void onDelete();
  });

  return useMemo(
    () => ({ ...actionRegistry[supportTicketActionIds.deleteDraft], execute }),
    [execute],
  );
}

/**
 * Hook that returns the reply action with a bound handler.
 */
export function useReplyAction(
  onReply: () => void | Promise<void>,
): SupportTicketAction {
  const execute = useHandler(() => {
    void onReply();
  });

  return useMemo(
    () => ({ ...actionRegistry[supportTicketActionIds.reply], execute }),
    [execute],
  );
}

/**
 * Hook that returns the reply all action with a bound handler.
 */
export function useReplyAllAction(
  onReplyAll: () => void | Promise<void>,
): SupportTicketAction {
  const execute = useHandler(() => {
    void onReplyAll();
  });

  return useMemo(
    () => ({ ...actionRegistry[supportTicketActionIds.replyAll], execute }),
    [execute],
  );
}

/**
 * Hook that returns the forward action with a bound handler.
 */
export function useForwardAction(
  onForward: () => void | Promise<void>,
): SupportTicketAction {
  const execute = useHandler(() => {
    void onForward();
  });

  return useMemo(
    () => ({ ...actionRegistry[supportTicketActionIds.forward], execute }),
    [execute],
  );
}

/**
 * Hook that returns the internal note action with a bound handler.
 */
export function useInternalNoteAction(
  onInternalNote: () => void | Promise<void>,
): SupportTicketAction {
  const execute = useHandler(() => {
    void onInternalNote();
  });

  return useMemo(
    () => ({ ...actionRegistry[supportTicketActionIds.internalNote], execute }),
    [execute],
  );
}

/**
 * Type-safe map of action IDs to execute handlers.
 * Handlers are bound to a specific support ticket ID, so they take no arguments.
 */
export type SupportTicketActionHandlerMap = Partial<
  Record<ActionId, () => void>
>;

/**
 * Hook that returns a map of action IDs to handlers bound to a specific support ticket.
 * Used by ScopeMenu and keyboard shortcut system to execute actions.
 */
export function useSupportTicketActionHandlers(
  supportTicketId: SupportTicketId,
  onMergeTicket?: () => void,
): SupportTicketActionHandlerMap {
  const {
    close,
    reopen,
    markInProgress,
    snooze,
    markRead,
    markUnread,
    archive,
    delete: deleteAction,
    setPriority,
  } = useSupportTicketActions();
  const markSpam = useMarkSpamSupportTicketAction();
  const unmarkSpam = useUnmarkSpamSupportTicketAction();
  const assignToMe = useAssignToMeSupportTicketAction();

  const { pushScope } = useActionScope();

  return useMemo(
    (): SupportTicketActionHandlerMap => ({
      // Direct actions
      [supportTicketActionIds.close]: () => close.execute(supportTicketId),
      [supportTicketActionIds.reopen]: () => reopen.execute(supportTicketId),
      [supportTicketActionIds.markInProgress]: () =>
        markInProgress.execute(supportTicketId),
      [supportTicketActionIds.markRead]: () =>
        markRead.execute(supportTicketId),
      [supportTicketActionIds.markUnread]: () =>
        markUnread.execute(supportTicketId),
      [supportTicketActionIds.archive]: () => archive.execute(supportTicketId),
      [supportTicketActionIds.delete]: () =>
        deleteAction.execute(supportTicketId),
      [supportTicketActionIds.markSpam]: () => {
        void markSpam.execute(supportTicketId);
      },
      [supportTicketActionIds.unmarkSpam]: () => {
        void unmarkSpam.execute(supportTicketId);
      },
      [supportTicketActionIds.assignToMe]: () =>
        assignToMe.execute(supportTicketId),

      [supportTicketActionIds.mergeTicket]: () => onMergeTicket?.(),

      [supportTicketActionIds.setPriority]: () => pushScope(priorityScope),
      [supportTicketActionIds.assign]: () => pushScope(assignScope),
      [supportTicketActionIds.addTag]: () => pushScope(addTagScope),
      [supportTicketActionIds.snooze]: () => pushScope(snoozeScope),

      [supportTicketActionIds.priorityUrgent]: () =>
        setPriority.execute(supportTicketId, "URGENT"),
      [supportTicketActionIds.priorityClear]: () =>
        setPriority.execute(supportTicketId, null),

      // Snooze options
      [supportTicketActionIds.snooze1h]: () =>
        snooze.execute(supportTicketId, { duration: "1h" }),
      [supportTicketActionIds.snooze4h]: () =>
        snooze.execute(supportTicketId, { duration: "4h" }),
      [supportTicketActionIds.snooze24h]: () =>
        snooze.execute(supportTicketId, { duration: "24h" }),
      // snoozeCustom is handled by the modal in SupportTicketActionsDropdown
      [supportTicketActionIds.snoozeCustom]: () => undefined,
      [supportTicketActionIds.snoozeClear]: () =>
        snooze.execute(supportTicketId, null),

      // Table navigation - these don't operate on a single support ticket
      // They should be handled separately by the table component
    }),
    [
      supportTicketId,
      close,
      reopen,
      markInProgress,
      snooze,
      markRead,
      markUnread,
      archive,
      deleteAction,
      setPriority,
      pushScope,
      onMergeTicket,
      markSpam,
      unmarkSpam,
      assignToMe,
    ],
  );
}
